import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Contact() {
  const [activeTab, setActiveTab] = useState('contact');
  
  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Booking Form State
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    preferred_date: null,
    preferred_time: '',
    project_description: '',
    address: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Maintenance Form State
  const [maintenanceForm, setMaintenanceForm] = useState({
    name: '',
    email: '',
    phone: '',
    property_address: '',
    issue_type: '',
    urgency: '',
    description: '',
    preferred_contact_method: ''
  });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceSuccess, setMaintenanceSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    try {
      const data = {
        ...bookingForm,
        preferred_date: bookingForm.preferred_date ? bookingForm.preferred_date.toISOString().split('T')[0] : ''
      };
      await axios.post(`${API}/booking`, data);
      setBookingSuccess(true);
      toast.success('Consultation booked successfully! We will confirm your appointment shortly.');
      setBookingForm({
        name: '', email: '', phone: '', service_type: '',
        preferred_date: null, preferred_time: '', project_description: '', address: ''
      });
    } catch (error) {
      toast.error('Failed to book consultation. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    setMaintenanceLoading(true);
    try {
      await axios.post(`${API}/maintenance`, maintenanceForm);
      setMaintenanceSuccess(true);
      toast.success('Maintenance request submitted! Our team will contact you soon.');
      setMaintenanceForm({
        name: '', email: '', phone: '', property_address: '',
        issue_type: '', urgency: '', description: '', preferred_contact_method: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const inputStyles = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main placeholder:text-text-muted/50 focus:border-primary focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0";
  const labelStyles = "font-mono text-xs uppercase tracking-widest text-text-muted";

  return (
    <main data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-surface">
        <div className="absolute inset-0 grid-lines opacity-50" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="overline mb-6 block">Get in Touch</span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl text-text-main tracking-tight">
              Contact <span className="text-primary">Us</span>
            </h1>
            <p className="mt-8 max-w-xl mx-auto text-text-muted font-body text-base md:text-lg leading-relaxed">
              Ready to start your project? Book a consultation, request maintenance, or simply get in touch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <motion.div
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className="lg:col-span-1"
            >
              <span className="overline">Contact Information</span>
              <h2 className="font-heading font-light text-3xl md:text-4xl text-text-main mt-4 tracking-tight">
                Let's Talk
              </h2>
              
              <div className="mt-10 space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">Phone</div>
                    <div className="flex flex-col gap-1">
                      <a href="tel:0825697495" className="text-text-main hover:text-primary transition-colors text-base">
                        082 569 7495 <span className="text-text-muted text-xs font-mono">/ Moazzum</span>
                      </a>
                      <a href="tel:0690236511" className="text-text-main hover:text-primary transition-colors text-base">
                        069 023 6511 <span className="text-text-muted text-xs font-mono">/ Sedick</span>
                      </a>
                      <a href="tel:0696079563" className="text-text-main hover:text-primary transition-colors text-base">
                        069 607 9563 <span className="text-text-muted text-xs font-mono">/ Mogamad Raee</span>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">Email</div>
                    <a href="mailto:info@MBConstruction.co.za" className="text-text-main hover:text-primary transition-colors text-lg">
                      info@MBConstruction.co.za
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">Address</div>
                    <p className="text-text-main text-lg">
                      220 Main Road<br />
                      Claremont, Cape Town, 7708<br />
                      South Africa
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">Hours</div>
                    <p className="text-text-main text-lg">
                      Mon - Fri: 8:00 - 17:00<br />
                      Sat: 8:00 - 13:00<br />
                      <span className="text-text-muted text-sm">Sun: Emergency Only</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Forms */}
            <motion.div
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full grid grid-cols-3 bg-surface border border-border rounded-none h-auto p-0">
                  <TabsTrigger 
                    value="contact" 
                    data-testid="tab-contact"
                    className="font-mono text-xs uppercase tracking-widest py-4 rounded-none data-[state=active]:bg-primary data-[state=active]:text-background"
                  >
                    Contact
                  </TabsTrigger>
                  <TabsTrigger 
                    value="booking" 
                    data-testid="tab-booking"
                    className="font-mono text-xs uppercase tracking-widest py-4 rounded-none data-[state=active]:bg-primary data-[state=active]:text-background"
                  >
                    Book Consultation
                  </TabsTrigger>
                  <TabsTrigger 
                    value="maintenance" 
                    data-testid="tab-maintenance"
                    className="font-mono text-xs uppercase tracking-widest py-4 rounded-none data-[state=active]:bg-primary data-[state=active]:text-background"
                  >
                    Maintenance
                  </TabsTrigger>
                </TabsList>

                {/* Contact Form */}
                <TabsContent value="contact" className="mt-8">
                  {contactSuccess ? (
                    <div className="text-center py-16 bg-surface border border-border p-8">
                      <CheckCircle size={48} className="text-accent-success mx-auto mb-4" />
                      <h3 className="font-heading text-2xl text-text-main mb-2">Message Sent!</h3>
                      <p className="text-text-muted">We'll get back to you within 24 hours.</p>
                      <button
                        onClick={() => setContactSuccess(false)}
                        className="mt-6 text-primary font-mono text-xs uppercase tracking-widest hover:underline"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-8 bg-surface border border-border p-8 md:p-12">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="contact-name" className={labelStyles}>Full Name *</Label>
                          <Input
                            id="contact-name"
                            data-testid="contact-name-input"
                            required
                            value={contactForm.name}
                            onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                            className={inputStyles}
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-email" className={labelStyles}>Email *</Label>
                          <Input
                            id="contact-email"
                            data-testid="contact-email-input"
                            type="email"
                            required
                            value={contactForm.email}
                            onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                            className={inputStyles}
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="contact-phone" className={labelStyles}>Phone</Label>
                          <Input
                            id="contact-phone"
                            data-testid="contact-phone-input"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                            className={inputStyles}
                            placeholder="+27 12 345 6789"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-subject" className={labelStyles}>Subject *</Label>
                          <Input
                            id="contact-subject"
                            data-testid="contact-subject-input"
                            required
                            value={contactForm.subject}
                            onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                            className={inputStyles}
                            placeholder="Project Inquiry"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="contact-message" className={labelStyles}>Message *</Label>
                        <Textarea
                          id="contact-message"
                          data-testid="contact-message-input"
                          required
                          rows={5}
                          value={contactForm.message}
                          onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                          className={`${inputStyles} resize-none`}
                          placeholder="Tell us about your project..."
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="contact-submit-btn"
                        disabled={contactLoading}
                        className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {contactLoading ? 'Sending...' : 'Send Message'}
                        <Send size={14} />
                      </button>
                    </form>
                  )}
                </TabsContent>

                {/* Booking Form */}
                <TabsContent value="booking" className="mt-8">
                  {bookingSuccess ? (
                    <div className="text-center py-16 bg-surface border border-border p-8">
                      <CheckCircle size={48} className="text-accent-success mx-auto mb-4" />
                      <h3 className="font-heading text-2xl text-text-main mb-2">Consultation Booked!</h3>
                      <p className="text-text-muted">We'll confirm your appointment shortly.</p>
                      <button
                        onClick={() => setBookingSuccess(false)}
                        className="mt-6 text-primary font-mono text-xs uppercase tracking-widest hover:underline"
                      >
                        Book Another Consultation
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleBookingSubmit} className="space-y-8 bg-surface border border-border p-8 md:p-12">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="booking-name" className={labelStyles}>Full Name *</Label>
                          <Input
                            id="booking-name"
                            data-testid="booking-name-input"
                            required
                            value={bookingForm.name}
                            onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                            className={inputStyles}
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <Label htmlFor="booking-email" className={labelStyles}>Email *</Label>
                          <Input
                            id="booking-email"
                            data-testid="booking-email-input"
                            type="email"
                            required
                            value={bookingForm.email}
                            onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                            className={inputStyles}
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="booking-phone" className={labelStyles}>Phone *</Label>
                          <Input
                            id="booking-phone"
                            data-testid="booking-phone-input"
                            required
                            value={bookingForm.phone}
                            onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                            className={inputStyles}
                            placeholder="+27 12 345 6789"
                          />
                        </div>
                        <div>
                          <Label htmlFor="booking-service" className={labelStyles}>Service Type *</Label>
                          <Select 
                            value={bookingForm.service_type} 
                            onValueChange={(value) => setBookingForm({...bookingForm, service_type: value})}
                          >
                            <SelectTrigger 
                              data-testid="booking-service-select"
                              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main focus:ring-0"
                            >
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-border">
                              <SelectItem value="roofing">Roofing & Waterproofing</SelectItem>
                              <SelectItem value="plumbing">Plumbing Services</SelectItem>
                              <SelectItem value="maintenance">General Maintenance</SelectItem>
                              <SelectItem value="construction">Building Construction</SelectItem>
                              <SelectItem value="asbestos">Asbestos Removal</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label className={labelStyles}>Preferred Date *</Label>
                          <div className="mt-4">
                            <Calendar
                              mode="single"
                              selected={bookingForm.preferred_date}
                              onSelect={(date) => setBookingForm({...bookingForm, preferred_date: date})}
                              disabled={(date) => date < new Date()}
                              data-testid="booking-calendar"
                              className="bg-background border border-border p-4"
                            />
                          </div>
                        </div>
                        <div className="space-y-8">
                          <div>
                            <Label htmlFor="booking-time" className={labelStyles}>Preferred Time *</Label>
                            <Select 
                              value={bookingForm.preferred_time} 
                              onValueChange={(value) => setBookingForm({...bookingForm, preferred_time: value})}
                            >
                              <SelectTrigger 
                                data-testid="booking-time-select"
                                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main focus:ring-0"
                              >
                                <SelectValue placeholder="Select a time" />
                              </SelectTrigger>
                              <SelectContent className="bg-surface border-border">
                                <SelectItem value="08:00">08:00</SelectItem>
                                <SelectItem value="09:00">09:00</SelectItem>
                                <SelectItem value="10:00">10:00</SelectItem>
                                <SelectItem value="11:00">11:00</SelectItem>
                                <SelectItem value="12:00">12:00</SelectItem>
                                <SelectItem value="14:00">14:00</SelectItem>
                                <SelectItem value="15:00">15:00</SelectItem>
                                <SelectItem value="16:00">16:00</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="booking-address" className={labelStyles}>Property Address</Label>
                            <Input
                              id="booking-address"
                              data-testid="booking-address-input"
                              value={bookingForm.address}
                              onChange={(e) => setBookingForm({...bookingForm, address: e.target.value})}
                              className={inputStyles}
                              placeholder="123 Example Street, City"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="booking-description" className={labelStyles}>Project Description *</Label>
                        <Textarea
                          id="booking-description"
                          data-testid="booking-description-input"
                          required
                          rows={4}
                          value={bookingForm.project_description}
                          onChange={(e) => setBookingForm({...bookingForm, project_description: e.target.value})}
                          className={`${inputStyles} resize-none`}
                          placeholder="Describe your project requirements..."
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="booking-submit-btn"
                        disabled={bookingLoading || !bookingForm.preferred_date || !bookingForm.service_type || !bookingForm.preferred_time}
                        className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {bookingLoading ? 'Booking...' : 'Book Consultation'}
                        <ArrowRight size={14} />
                      </button>
                    </form>
                  )}
                </TabsContent>

                {/* Maintenance Form */}
                <TabsContent value="maintenance" className="mt-8">
                  {maintenanceSuccess ? (
                    <div className="text-center py-16 bg-surface border border-border p-8">
                      <CheckCircle size={48} className="text-accent-success mx-auto mb-4" />
                      <h3 className="font-heading text-2xl text-text-main mb-2">Request Submitted!</h3>
                      <p className="text-text-muted">Our maintenance team will contact you shortly.</p>
                      <button
                        onClick={() => setMaintenanceSuccess(false)}
                        className="mt-6 text-primary font-mono text-xs uppercase tracking-widest hover:underline"
                      >
                        Submit Another Request
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleMaintenanceSubmit} className="space-y-8 bg-surface border border-border p-8 md:p-12">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="maintenance-name" className={labelStyles}>Full Name *</Label>
                          <Input
                            id="maintenance-name"
                            data-testid="maintenance-name-input"
                            required
                            value={maintenanceForm.name}
                            onChange={(e) => setMaintenanceForm({...maintenanceForm, name: e.target.value})}
                            className={inputStyles}
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <Label htmlFor="maintenance-email" className={labelStyles}>Email *</Label>
                          <Input
                            id="maintenance-email"
                            data-testid="maintenance-email-input"
                            type="email"
                            required
                            value={maintenanceForm.email}
                            onChange={(e) => setMaintenanceForm({...maintenanceForm, email: e.target.value})}
                            className={inputStyles}
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="maintenance-phone" className={labelStyles}>Phone *</Label>
                          <Input
                            id="maintenance-phone"
                            data-testid="maintenance-phone-input"
                            required
                            value={maintenanceForm.phone}
                            onChange={(e) => setMaintenanceForm({...maintenanceForm, phone: e.target.value})}
                            className={inputStyles}
                            placeholder="+27 12 345 6789"
                          />
                        </div>
                        <div>
                          <Label htmlFor="maintenance-address" className={labelStyles}>Property Address *</Label>
                          <Input
                            id="maintenance-address"
                            data-testid="maintenance-address-input"
                            required
                            value={maintenanceForm.property_address}
                            onChange={(e) => setMaintenanceForm({...maintenanceForm, property_address: e.target.value})}
                            className={inputStyles}
                            placeholder="123 Example Street, City"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <Label htmlFor="maintenance-issue" className={labelStyles}>Issue Type *</Label>
                          <Select 
                            value={maintenanceForm.issue_type} 
                            onValueChange={(value) => setMaintenanceForm({...maintenanceForm, issue_type: value})}
                          >
                            <SelectTrigger 
                              data-testid="maintenance-issue-select"
                              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main focus:ring-0"
                            >
                              <SelectValue placeholder="Select issue type" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-border">
                              <SelectItem value="plumbing">Plumbing</SelectItem>
                              <SelectItem value="electrical">Electrical</SelectItem>
                              <SelectItem value="roofing">Roofing/Waterproofing</SelectItem>
                              <SelectItem value="painting">Painting/Decorating</SelectItem>
                              <SelectItem value="doors-windows">Doors & Windows</SelectItem>
                              <SelectItem value="flooring">Flooring</SelectItem>
                              <SelectItem value="general">General Repairs</SelectItem>
                              <SelectItem value="inspection">Property Inspection</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="maintenance-urgency" className={labelStyles}>Urgency *</Label>
                          <Select 
                            value={maintenanceForm.urgency} 
                            onValueChange={(value) => setMaintenanceForm({...maintenanceForm, urgency: value})}
                          >
                            <SelectTrigger 
                              data-testid="maintenance-urgency-select"
                              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main focus:ring-0"
                            >
                              <SelectValue placeholder="Select urgency" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-border">
                              <SelectItem value="emergency">Emergency (24hr)</SelectItem>
                              <SelectItem value="urgent">Urgent (48hr)</SelectItem>
                              <SelectItem value="standard">Standard (Within 1 week)</SelectItem>
                              <SelectItem value="low">Low (Schedule at convenience)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="maintenance-contact" className={labelStyles}>Preferred Contact Method *</Label>
                        <Select 
                          value={maintenanceForm.preferred_contact_method} 
                          onValueChange={(value) => setMaintenanceForm({...maintenanceForm, preferred_contact_method: value})}
                        >
                          <SelectTrigger 
                            data-testid="maintenance-contact-select"
                            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-text-main focus:ring-0"
                          >
                            <SelectValue placeholder="Select contact method" />
                          </SelectTrigger>
                          <SelectContent className="bg-surface border-border">
                            <SelectItem value="phone">Phone Call</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="maintenance-description" className={labelStyles}>Issue Description *</Label>
                        <Textarea
                          id="maintenance-description"
                          data-testid="maintenance-description-input"
                          required
                          rows={4}
                          value={maintenanceForm.description}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                          className={`${inputStyles} resize-none`}
                          placeholder="Please describe the issue in detail..."
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="maintenance-submit-btn"
                        disabled={maintenanceLoading || !maintenanceForm.issue_type || !maintenanceForm.urgency || !maintenanceForm.preferred_contact_method}
                        className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {maintenanceLoading ? 'Submitting...' : 'Submit Request'}
                        <ArrowRight size={14} />
                      </button>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
